'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type Category = 'All' | 'Affordable' | 'Attainable' | 'Market-Rate' | 'Senior Living';

interface Property {
  name: string;
  location: string;
  category: Exclude<Category, 'All'>;
  units: number;
  year: number;
  status: 'Completed' | 'In Development';
  image: string;
  alt: string;
  description: string;
}

const properties: Property[] = [
{
  name: 'Riverside Commons',
  location: 'Chicago, IL',
  category: 'Affordable',
  units: 120,
  year: 2022,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c49152f-1772377978285.png",
  alt: 'Riverside Commons affordable housing complex in Chicago with modern brick facade and green landscaping',
  description: 'A 120-unit affordable community offering one-, two-, and three-bedroom apartments steps from public transit and neighborhood amenities.'
},
{
  name: 'Elmwood Heights',
  location: 'Detroit, MI',
  category: 'Affordable',
  units: 96,
  year: 2021,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1758b4ec5-1776834605680.png",
  alt: 'Elmwood Heights residential community in Detroit featuring contemporary townhome-style affordable housing',
  description: 'Townhome-style affordable housing in a revitalized Detroit neighborhood, featuring on-site resident services and community gardens.'
},
{
  name: 'Meridian Place',
  location: 'Columbus, OH',
  category: 'Attainable',
  units: 200,
  year: 2023,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e00b26f9-1779936772450.png",
  alt: 'Meridian Place attainable housing development in Columbus Ohio with mixed-income apartment building',
  description: 'A mixed-income development offering workforce housing at below-market rents, with access to a fitness center, co-working space, and rooftop terrace.'
},
{
  name: 'Northgate Flats',
  location: 'Minneapolis, MN',
  category: 'Attainable',
  units: 148,
  year: 2024,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1979c2c92-1779936772283.png",
  alt: 'Northgate Flats modern attainable housing in Minneapolis with clean architectural lines and urban setting',
  description: 'Attainable apartments designed for working families and young professionals, located near major employment corridors and transit hubs.'
},
{
  name: 'The Harlow',
  location: 'Nashville, TN',
  category: 'Market-Rate',
  units: 250,
  year: 2023,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d09f730-1778349608861.png",
  alt: 'The Harlow luxury market-rate apartment building in Nashville with upscale amenities and modern design',
  description: 'A premier market-rate community in the heart of Nashville, featuring resort-style amenities, concierge services, and curated retail on the ground floor.'
},
{
  name: 'Lakeview Residences',
  location: 'Milwaukee, WI',
  category: 'Market-Rate',
  units: 180,
  year: 2022,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10807dee8-1779936772500.png",
  alt: 'Lakeview Residences market-rate apartments in Milwaukee with lakefront views and contemporary architecture',
  description: 'Waterfront market-rate living with panoramic lake views, private balconies, and a vibrant ground-floor retail corridor.'
},
{
  name: 'Sunrise Village',
  location: 'Phoenix, AZ',
  category: 'Senior Living',
  units: 110,
  year: 2021,
  status: 'Completed',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11775f12a-1776834607106.png",
  alt: 'Sunrise Village senior living community in Phoenix Arizona with single-story accessible design and courtyard',
  description: 'An active adult community designed for residents 55+, featuring accessible design, wellness programming, and a vibrant social calendar.'
},
{
  name: 'Maple Grove Senior Homes',
  location: 'St. Louis, MO',
  category: 'Senior Living',
  units: 88,
  year: 2026,
  status: 'In Development',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cba691e3-1776834606540.png",
  alt: 'Maple Grove Senior Homes development rendering in St. Louis showing accessible senior housing with garden areas',
  description: 'Currently in development, Maple Grove will offer affordable senior housing with on-site health services, transportation assistance, and community programming.'
}];

const categories: Category[] = ['All', 'Affordable', 'Attainable', 'Market-Rate', 'Senior Living'];

const categoryColors: Record<Exclude<Category, 'All'>, string> = {
  'Affordable': '#003c82',
  'Attainable': '#00b195',
  'Market-Rate': '#f05323',
  'Senior Living': '#6dcff6'
};

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');

  const filtered = activeCategory === 'All' ?
  properties :
  properties.filter((p) => p.category === activeCategory);

  return (
    <main style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <Header />

      {/* Hero */}
      <section className="relative flex items-end overflow-hidden pt-20 min-h-[320px] md:min-h-[400px] lg:min-h-[480px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&h=700&fit=crop)',
            backgroundPosition: 'center 30%'
          }} />

        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(45deg, rgba(0,60,130,0.85) 0%, rgba(0,60,130,0.45) 60%, rgba(0,0,0,0.1) 100%)' }} />

        <div
          className="absolute bottom-0 right-0 w-2/5 h-16 md:h-24 lg:h-[100px]"
          style={{ backgroundColor: '#6dcff6', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }} />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14 lg:pb-16">
          <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#6dcff6' }}>
            Our Work
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-normal text-white leading-tight m-0 max-w-xl">
            Homes built with <em className="italic" style={{ color: '#6dcff6' }}>purpose</em> and precision.
          </h1>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 md:py-10" style={{ backgroundColor: '#003c82' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-center">
            {[
            { stat: '20,000+', label: 'Homes Developed' },
            { stat: '150+', label: 'Communities' },
            { stat: '18', label: 'States' },
            { stat: '4', label: 'Housing Types' }].
            map((item, i) =>
            <div
              key={item.label}
              className="py-5 px-3 md:px-5"
              style={{ borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>

                <div className="text-3xl md:text-4xl font-bold leading-none" style={{ color: '#6dcff6' }}>{item.stat}</div>
                <div className="text-xs mt-2 tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>{item.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-12 md:py-16 lg:py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 md:mb-12">
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '10px 22px',
                fontSize: '13px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '0.04em',
                border: 'none',
                borderRadius: '2px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: activeCategory === cat ? '#003c82' : '#ffffff',
                color: activeCategory === cat ? '#ffffff' : '#333333',
                boxShadow: activeCategory === cat ? '0 4px 12px rgba(0,60,130,0.25)' : '0 1px 4px rgba(0,0,0,0.08)'
              }}>

                {cat}
              </button>
            )}
          </div>

          {/* Property Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((property) =>
            <div
              key={property.name}
              className="bg-white overflow-hidden cursor-pointer"
              style={{
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
              }}>

                {/* Image */}
                <div className="relative overflow-hidden h-48 sm:h-52 md:h-56">
                  <img
                  src={property.image}
                  alt={property.alt}
                  className="w-full h-full object-cover block"
                  style={{ transition: 'transform 0.4s ease' }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1.05)';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';}} />

                  {/* Category Badge */}
                  <div
                  className="absolute top-4 left-4 text-white text-xs font-bold tracking-wider uppercase px-3 py-1"
                  style={{ backgroundColor: categoryColors[property.category], borderRadius: '2px' }}>

                    {property.category}
                  </div>
                  {/* Status Badge */}
                  {property.status === 'In Development' &&
                <div
                  className="absolute top-4 right-4 text-white text-xs font-bold tracking-wide px-3 py-1"
                  style={{ backgroundColor: '#f05323', borderRadius: '2px' }}>

                      In Development
                    </div>
                }
                </div>

                {/* Content */}
                <div className="p-5 md:p-6">
                  <h3 className="text-base md:text-lg font-semibold mb-1" style={{ color: '#003c82' }}>{property.name}</h3>
                  <p className="text-xs md:text-sm mb-3 flex items-center gap-1" style={{ color: '#888' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    {property.location}
                  </p>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: '#666' }}>{property.description}</p>
                  <div className="flex gap-6 border-t pt-4" style={{ borderColor: '#f0f0f0' }}>
                    <div>
                      <div className="text-lg md:text-xl font-bold" style={{ color: '#003c82' }}>{property.units}</div>
                      <div className="text-xs tracking-wide" style={{ color: '#888' }}>Units</div>
                    </div>
                    <div>
                      <div className="text-lg md:text-xl font-bold" style={{ color: '#003c82' }}>{property.year}</div>
                      <div className="text-xs tracking-wide" style={{ color: '#888' }}>Year</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 lg:py-20 bg-white border-t" style={{ borderColor: '#e0e0e0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#6dcff6' }}>
                Partner With Us
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-normal leading-tight mb-5" style={{ color: '#003c82' }}>
                Interested in investing or developing with Brickwell?
              </h2>
              <p className="text-sm md:text-base leading-relaxed mb-8" style={{ color: '#555' }}>
                We're always looking for new partners, properties, and opportunities to expand access to quality housing. Let's talk about what we can build together.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 text-white text-sm font-semibold no-underline"
                style={{
                  backgroundColor: '#003c82',
                  padding: '14px 32px',
                  borderRadius: '2px',
                  letterSpacing: '0.02em'
                }}>

                Get in Touch
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <div className="relative mt-8 md:mt-0">
              <img
                src="/assets/images/P2-1776941702342.jpg"
                alt="Brickwell Housing development team reviewing site plans for a new housing community project"
                className="w-full object-cover block h-64 sm:h-80 md:h-96" />

              <div
                className="absolute hidden md:block"
                style={{
                  top: '-24px',
                  right: '-24px',
                  width: '160px',
                  height: '160px',
                  backgroundColor: 'rgba(109,207,246,0.2)',
                  zIndex: -1
                }} />

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>);

}